import React, { useState, useEffect } from 'react';
import Icon from '../AppIcon';

const XPProgressHeader = ({ currentXP = 0, level = 'Newbie', totalXP = 1000, recentGain = 0 }) => {
  const [showGainAnimation, setShowGainAnimation] = useState(false);
  const [displayedXP, setDisplayedXP] = useState(currentXP);

  const progressPercentage = Math.min((currentXP / totalXP) * 100, 100);

  useEffect(() => {
    if (recentGain > 0) {
      setShowGainAnimation(true);
      const timer = setTimeout(() => {
        setShowGainAnimation(false);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [recentGain]);

  useEffect(() => {
    if (currentXP !== displayedXP) {
      const increment = (currentXP - displayedXP) / 20;
      const timer = setInterval(() => {
        setDisplayedXP((prev) => {
          const next = prev + increment;
          if ((increment > 0 && next >= currentXP) || (increment < 0 && next <= currentXP)) {
            clearInterval(timer);
            return currentXP;
          }
          return next;
        });
      }, 30);
      return () => clearInterval(timer);
    }
  }, [currentXP, displayedXP]);

  return (
    <header className="fixed top-0 left-0 lg:left-60 right-0 h-20 bg-card/95 backdrop-blur-sm border-b border-border z-40 shadow-elevation-1">
      <div className="h-full px-6 flex items-center justify-between">
        <div className="flex items-center gap-4 flex-1">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center ring-2 ring-primary/20">
              <Icon name="Zap" size={24} color="var(--color-primary)" strokeWidth={2.5} />
            </div>
            <div className="flex flex-col">
              <span className="text-sm text-muted-foreground font-medium">Level</span>
              <span className="text-lg font-bold text-foreground">{level}</span>
            </div>
          </div>

          <div className="hidden md:flex flex-col flex-1 max-w-md ml-6">
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm font-medium text-foreground">
                {Math.round(displayedXP)} / {totalXP} XP
              </span>
              <span className="text-xs text-muted-foreground">
                {Math.round(progressPercentage)}%
              </span>
            </div>
            <div className="relative h-2 bg-muted rounded-full overflow-hidden">
              <div
                className="absolute top-0 left-0 h-full bg-gradient-to-r from-primary to-warning rounded-full transition-all duration-300 ease-spring shadow-glow"
                style={{ width: `${progressPercentage}%` }}
              />
            </div>
          </div>
        </div>

        {showGainAnimation && recentGain > 0 && (
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 animate-scale-in">
            <div className="bg-success/20 text-success px-4 py-2 rounded-lg shadow-elevation-2 flex items-center gap-2 border border-success/30">
              <Icon name="TrendingUp" size={20} color="var(--color-success)" />
              <span className="font-bold text-lg">+{recentGain} XP</span>
            </div>
          </div>
        )}

        <div className="flex items-center gap-3">
          <button
            className="p-2 rounded-lg hover:bg-muted/50 transition-colors duration-150"
            aria-label="Notifications"
          >
            <Icon name="Bell" size={20} color="var(--color-muted-foreground)" />
          </button>
          <button
            className="p-2 rounded-lg hover:bg-muted/50 transition-colors duration-150"
            aria-label="Settings"
          >
            <Icon name="Settings" size={20} color="var(--color-muted-foreground)" />
          </button>
          <div className="w-10 h-10 rounded-full bg-accent/20 flex items-center justify-center ring-2 ring-accent/30 cursor-pointer hover:ring-accent/50 transition-all duration-150">
            <Icon name="User" size={20} color="var(--color-accent)" />
          </div>
        </div>
      </div>
    </header>
  );
};

export default XPProgressHeader;